import { Mic, MicOff, RotateCcw, Send, Volume2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import type { LiveMatchEventType, Player } from './types';
import { parseVoiceCommand, type VoiceCommandResult } from './voiceCommands';
import { useI18n } from './I18nContext';

type SpeechRecognitionCtor = new () => {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: any) => void) | null;
  onerror: ((event: any) => void) | null;
  onend: (() => void) | null;
};

export function LiveVoiceLogger({
  players,
  onCommand,
  onVoiceSessionStart,
}: {
  players: Player[];
  onCommand: (player: Player, eventType: LiveMatchEventType, transcript: string) => void;
  onVoiceSessionStart?: () => void;
}) {
  const { language } = useI18n();
  const [listening, setListening] = useState(false);
  const [commandText, setCommandText] = useState('');
  const [lastResult, setLastResult] = useState<VoiceCommandResult | null>(null);
  const [recognitionLanguage, setRecognitionLanguage] = useState<'en-GB' | 'pl-PL'>(language === 'pl' ? 'pl-PL' : 'en-GB');
  const recognitionRef = useRef<any>(null);

  const speechSupported = useMemo(() => {
    const w = window as any;
    return Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);
  }, []);

  function submit(text: string) {
    const result = parseVoiceCommand(text, players);
    setLastResult(result);
    if (result.ok && result.player && result.eventType) {
      onCommand(result.player, result.eventType, text);
      setCommandText('');
    }
  }

  function startListening() {
    const w = window as any;
    const Ctor: SpeechRecognitionCtor | undefined = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!Ctor) {
      setLastResult({ ok: false, transcript: '', confidence: 'low', message: 'Speech recognition unavailable. Use command text.' });
      return;
    }

    try {
      const recognition = new Ctor();
      recognition.lang = recognitionLanguage;
      recognition.interimResults = false;
      recognition.continuous = false;
      recognition.maxAlternatives = 3;
      recognition.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript || '';
        setCommandText(transcript);
        submit(transcript);
      };
      recognition.onerror = (event: any) => {
        setLastResult({ ok: false, transcript: '', confidence: 'low', message: `Voice error: ${event.error || 'unknown'}` });
        setListening(false);
      };
      recognition.onend = () => setListening(false);
      recognitionRef.current = recognition;
      onVoiceSessionStart?.();
      setListening(true);
      recognition.start();
    } catch {
      setListening(false);
    }
  }

  function stopListening() {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  }

  const examples = recognitionLanguage === 'pl-PL'
    ? ['81 strzał celny', 'Tommy asysta', '10 wygrany pojedynek']
    : ['81 shot on target', 'Tommy assist', '10 duel won'];

  return (
    <article className="metal-card voice-match-logger">
      <div className="voice-logger-head">
        <div>
          <p className="eyebrow">LIVE MATCH INTELLIGENCE · EN / PL</p>
          <h2>Voice Match Logger</h2>
          <p className="muted">Name, surname, nickname or shirt number + action.</p>
        </div>
        <div className="voice-language-toggle">
          <button className={recognitionLanguage === 'en-GB' ? 'active' : ''} onClick={() => setRecognitionLanguage('en-GB')}>EN</button>
          <button className={recognitionLanguage === 'pl-PL' ? 'active' : ''} onClick={() => setRecognitionLanguage('pl-PL')}>PL</button>
        </div>
      </div>

      <button
        type="button"
        className={`voice-mic-button ${listening ? 'listening' : ''}`}
        onClick={listening ? stopListening : startListening}
      >
        {listening ? <MicOff size={32} /> : <Mic size={32} />}
        <span>{listening ? 'Listening…' : speechSupported ? 'Tap & speak' : 'Voice unavailable'}</span>
      </button>

      <form className="voice-command-form" onSubmit={(event) => { event.preventDefault(); submit(commandText); }}>
        <input
          value={commandText}
          onChange={(event) => setCommandText(event.target.value)}
          placeholder={recognitionLanguage === 'pl-PL' ? 'np. 81 strzał celny' : 'e.g. 81 shot on target'}
        />
        <button type="submit"><Send size={17} /> Log</button>
      </form>

      <div className="voice-command-examples">
        {examples.map((example) => <button type="button" key={example} onClick={() => { setCommandText(example); submit(example); }}><Volume2 size={13}/>{example}</button>)}
      </div>

      {lastResult && (
        <div className={`voice-result ${lastResult.ok ? 'success' : 'warning'}`}>
          <strong>{lastResult.ok ? '✓ Logged' : 'Check command'}</strong>
          <span>{lastResult.message}</span>
          {lastResult.transcript && <small>“{lastResult.transcript}”</small>}
          <button type="button" onClick={() => setLastResult(null)}><RotateCcw size={14}/> Clear</button>
        </div>
      )}
    </article>
  );
}
